import { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import dayjs from 'dayjs';

import { BreadcrumbHeader, DetailPanel, StatusBadge, EmptyState, CreateVendorShipmentModal } from '../../components/shared';
import { partReplenishmentOrders, vendorShipments, warehouses, suppliers, parts, purchaseOrders as poArray, partReplenishmentMessages } from '../../services/dataClient';import ContentCard from "../../components/shared/ContentCard";

const fmt = (n) =>
n != null && n > 0 ?
new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) :
null;

// ─── Order Info card ──────────────────────────────────────────────────────────
function OrderInfoCard({ order }) {
  // Normalise parts: new orders have lineItems, old mock data has top-level fields
  const parts = order.lineItems?.length ?
  order.lineItems :
  [{ partNumber: order.partNumber, partDescription: order.partDescription, quantity: null, unitCost: null }];

  const summaryRows = [
  { label: 'Order #', value: order.orderNumber },
  { label: 'Supplier', value: order.vendorName },
  { label: 'Created Date', value: order.createdDate }];

  return (
    <ContentCard sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          Order Info
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'var(--grey-200)' }} />

      {/* Summary fields */}
      <Box sx={{ px: 3, pt: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {summaryRows.map(({ label, value }) =>
        <Box key={label} sx={{ display: 'flex', gap: 2 }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', minWidth: 130, flexShrink: 0 }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)' }}>
              {value ?? '—'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Parts table */}
      <Box sx={{ px: 3, pt: 2, pb: order.notes ? 1 : 2 }}>
        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mb: '10px' }}>
          Parts
        </Typography>
        <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '6px', overflow: 'hidden' }}>
          {/* Table header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 60px 90px', backgroundColor: '#eef2f6', px: 2, py: '8px', gap: 1 }}>
            {['Part #', 'Description', 'Qty', 'Unit Cost'].map((h) =>
            <Typography key={h} sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-600)' }}>
                {h}
              </Typography>
            )}
          </Box>
          {/* Table rows */}
          {parts.map((p, i) =>
          <Box
            key={i}
            sx={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 60px 90px',
              px: 2, py: '10px', gap: 1,
              borderTop: i > 0 ? '1px solid var(--grey-200)' : 'none'
            }}>
            
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                {p.partNumber || '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-700)' }}>
                {p.partDescription || '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-700)' }}>
                {p.quantity ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-700)' }}>
                {fmt(p.unitCost) ?? '—'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Notes */}
      {order.notes &&
      <Box sx={{ px: 3, pb: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', minWidth: 130, flexShrink: 0 }}>
              Notes
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)' }}>
              {order.notes}
            </Typography>
          </Box>
        </Box>
      }
    </ContentCard>);

}
// ─── Timeline builder ─────────────────────────────────────────────────────────
function buildTimeline(order, hasShipment) {
  const base = dayjs(order.createdDate);
  const events = [
  { label: 'Order Created', date: base.format('MMM D, YYYY') },
  { label: 'Submitted to Vendor', date: base.add(1, 'day').format('MMM D, YYYY') }];

  if (hasShipment) {
    events.push({ label: 'Shipment Dispatched', date: base.add(2, 'day').format('MMM D, YYYY') });
  }
  if (order.status === 'History') {
    events.push({ label: 'Order Completed', date: base.add(10, 'day').format('MMM D, YYYY') });
  }
  return events;
}

// ─── Vendor Shipment card ─────────────────────────────────────────────────────
function VendorShipmentCard({ shipment, onCreateClick }) {
  const navigate = useNavigate();

  return (
    <ContentCard
      sx={{ p: 3
      }}>
      
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
        Replenishment
      </Typography>
      <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />

      {shipment ?
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[
        { label: 'Shipment #', value: shipment.shipmentNumber },
        { label: 'Status', value: <StatusBadge status={shipment.status} /> },
        { label: 'Carrier', value: shipment.carrier ?? shipment.vendorName },
        { label: 'Tracking #', value: shipment.trackingNumber },
        { label: 'Est. Delivery', value: shipment.estimatedDeliveryDate }]
        .map(({ label, value }) =>
        <Box key={label} sx={{ display: 'flex', gap: 2 }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', minWidth: 130, flexShrink: 0 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)' }}>
                {value}
              </Typography>
            </Box>
        )}
          <Typography
          onClick={() => navigate(`/shipments/replenishment-shipments/${shipment.id}`)}
          sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', mt: 1,
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            '&:hover': { textDecoration: 'underline' }
          }}>
          
            <LocalShippingIcon sx={{ fontSize: 14 }} /> View Shipment
          </Typography>
        </Box> :

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <WarningAmberIcon sx={{ color: '#E65100', mt: '2px', fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#E65100' }}>
                Awaiting replenishment
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
                No replenishment has been created yet for this order.
              </Typography>
            </Box>
          </Box>
          <Button
          variant="contained"
          size="small"
          onClick={onCreateClick}
          sx={{ alignSelf: 'flex-start',
            backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none'
          }}>
          
            Create Replenishment Shipment
          </Button>
        </Box>
      }
    </ContentCard>);

}

// ─── Activity Timeline ────────────────────────────────────────────────────────
function ActivityTimeline({ events }) {
  return (
    <ContentCard
      sx={{ p: 3
      }}>
      
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
        Activity Timeline
      </Typography>
      <Divider sx={{ borderColor: 'var(--grey-200)', mb: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {events.map((ev, i) => {
          const isLast = i === events.length - 1;
          return (
            <Box key={ev.label} sx={{ display: 'flex', gap: '14px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 }}>
                <Box
                  sx={{ width: 12, height: 12, borderRadius: '50%', mt: '3px', flexShrink: 0,
                    backgroundColor: '#009688',
                    border: '2px solid #009688'
                  }} />
                
                {!isLast &&
                <Box sx={{ width: '2px', flex: 1, minHeight: 28, backgroundColor: 'var(--grey-200)', mt: '2px' }} />
                }
              </Box>
              <Box sx={{ pb: isLast ? 0 : 3 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                  {ev.label}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '2px' }}>
                  {ev.date}
                </Typography>
              </Box>
            </Box>);

        })}
      </Box>
    </ContentCard>);

}

// ─── Chat Panel ───────────────────────────────────────────────────────────────
function ChatPanel({ messages, recipient, onSend }) {
  const [inputText, setInputText] = useState('');
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || !recipient) return;
    onSend(text);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {e.preventDefault();handleSend();}
  };

  const fmtTime = (iso) => dayjs(iso).format('MMM D, h:mm A');

  return (
    <ContentCard sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ForumIcon sx={{ fontSize: 17, color: 'var(--grey-400)' }} />
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-900)' }}>Messages</Typography>
        {recipient &&
        <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: 'var(--grey-100)', borderRadius: '20px', px: '10px', py: '3px', ml: '4px' }}>
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-700)', fontWeight: 500 }}>{recipient.name}</Typography>
          </Box>
        }
      </Box>

      {/* Message area */}
      <Box
        ref={scrollRef}
        sx={{ height: 300, overflowY: 'auto', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: '12px', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { borderRadius: '4px', backgroundColor: 'var(--grey-300)' } }}>
        
        {!recipient &&
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center', px: 2 }}>
              No supplier assigned to this order.
            </Typography>
          </Box>
        }
        {recipient && messages.length === 0 &&
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center', px: 2 }}>
              No messages yet. Start the conversation.
            </Typography>
          </Box>
        }
        {messages.map((msg) => {
          const isMe = msg.sender === 'coordinator';
          return (
            <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
              <Box sx={{ maxWidth: '85%', px: '12px', py: '8px', borderRadius: isMe ? '8px 8px 2px 8px' : '8px 8px 8px 2px', backgroundColor: isMe ? '#009688' : 'var(--grey-100)', color: isMe ? '#fff' : 'var(--grey-900)' }}>
                <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.45, wordBreak: 'break-word' }}>{msg.text}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)', mt: '3px', px: '2px' }}>{fmtTime(msg.sentAt)}</Typography>
            </Box>);

        })}
      </Box>

      {/* Input row */}
      <Box sx={{ borderTop: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} />
        <IconButton size="small" disabled={!recipient} onClick={() => fileInputRef.current?.click()}
        sx={{ flexShrink: 0, ml: '10px', color: 'var(--grey-400)', '&:hover': { color: '#009688', backgroundColor: 'transparent' }, '&.Mui-disabled': { color: 'var(--grey-200)' } }}>
          <AttachFileIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <TextField size="small" placeholder="Type a message…" multiline maxRows={3} fullWidth
        disabled={!recipient} value={inputText}
        onChange={(e) => setInputText(e.target.value)} onKeyDown={handleKeyDown}
        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8125rem', borderRadius: 0, '& fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } }, '& .MuiInputBase-input': { py: '10px', px: '4px' } }} />
        
        <IconButton size="small" disabled={!inputText.trim() || !recipient} onClick={handleSend}
        sx={{ flexShrink: 0, mr: '10px', backgroundColor: '#009688', color: '#fff', borderRadius: '8px', width: 32, height: 32, '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-400)' } }}>
          <SendIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </ContentCard>);

}

const activeSuppliers = suppliers.filter((s) => s.active);
const EMPTY_PO_LINE = { partNumber: '', partDescription: '', quantity: '1', unitCost: '' };
const fmtCurrency = (n) =>
new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);
const fieldSx = {
  '& .MuiInputLabel-root.Mui-focused': { color: '#009688' },
  '& .MuiOutlinedInput-root': {
    fontSize: '0.875rem',
    '& fieldset': { borderColor: 'var(--grey-200)' },
    '&.Mui-focused fieldset': { borderColor: '#009688' }
  }
};

// ─── Linked PO card ────────────────────────────────────────────────────────────
function LinkedPOCard({ poId, poNumber, onCreateClick }) {
  const navigate = useNavigate();
  return (
    <ContentCard sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 2 }}>
        <ReceiptLongIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          Purchase Order
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />
      {poId ?
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', minWidth: 100, flexShrink: 0 }}>PO #</Typography>
            <Typography
            onClick={() => navigate(`/accounting/purchase-orders/${poId}`)}
            sx={{ fontSize: '0.875rem', color: '#009688', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            
              {poNumber}
            </Typography>
          </Box>
          <Typography
          onClick={() => navigate(`/accounting/purchase-orders/${poId}`)}
          sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', '&:hover': { textDecoration: 'underline' } }}>
          
            <ReceiptLongIcon sx={{ fontSize: 14 }} /> View Purchase Order
          </Typography>
        </Box> :

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
            No purchase order linked to this replenishment.
          </Typography>
          <Button variant="contained" size="small" onClick={onCreateClick}
        sx={{ alignSelf: 'flex-start', backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none' }}>
            Create PO
          </Button>
        </Box>
      }
    </ContentCard>);

}

// ─── Create PO modal (detail page) ────────────────────────────────────────────
function CreatePOModal({ open, order, onClose, onSave }) {
  const [vendorName, setVendorName] = useState('');
  const [lineItems, setLineItems] = useState([{ ...EMPTY_PO_LINE }]);
  const [notes, setNotes] = useState('');

  const handleEnter = () => {
    setVendorName(order?.vendorName ?? '');
    const seed = order?.lineItems?.length ?
    order.lineItems.map((li) => ({
      partNumber: li.partNumber ?? '',
      partDescription: li.partDescription ?? '',
      quantity: String(li.quantity ?? 1),
      unitCost: li.unitCost != null ? String(li.unitCost) : ''
    })) :
    [{ partNumber: order?.partNumber ?? '', partDescription: order?.partDescription ?? '', quantity: '1', unitCost: '' }];
    setLineItems(seed);
    setNotes('');
  };

  const updateLine = (idx, field, value) =>
  setLineItems((prev) => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));

  const handlePartChange = (idx, value) => {
    const desc = parts.find((p) => p.partNumber === value)?.description ?? '';
    setLineItems((prev) => prev.map((li, i) => i === idx ? { ...li, partNumber: value, partDescription: desc } : li));
  };

  const addLine = () => setLineItems((prev) => [...prev, { ...EMPTY_PO_LINE }]);
  const removeLine = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));

  const isValid = vendorName && lineItems.every((li) => li.partNumber && parseInt(li.quantity, 10) >= 1);

  const handleSave = () => {
    const built = lineItems.map((li) => ({
      partNumber: li.partNumber,
      partDescription: li.partDescription,
      quantity: parseInt(li.quantity, 10) || 1,
      unitCost: li.unitCost !== '' ? parseFloat(li.unitCost) : null
    }));
    const totalCost = built.reduce((s, li) => s + (li.unitCost ?? 0) * li.quantity, 0);
    const newPO = {
      id: `po-rep-${Date.now()}`,
      poNumber: `PO-${6000 + poArray.length + 1}`,
      vendorName,
      partNumber: built[0].partNumber,
      partDescription: built[0].partDescription,
      quantity: built[0].quantity,
      unitCost: built[0].unitCost ?? 0,
      totalCost,
      lineItems: built,
      status: 'Draft',
      date: dayjs().format('YYYY-MM-DD'),
      notes: notes || null,
      linkedOrderId: order?.id ?? null,
      linkedTicketNumber: order?.orderNumber ?? null
    };
    poArray.unshift(newPO);
    onSave(newPO);
  };

  const grandTotal = lineItems.reduce(
    (s, li) => s + (parseFloat(li.unitCost) || 0) * (parseInt(li.quantity, 10) || 0), 0
  );

  return (
    <Dialog open={open} onClose={(e, r) => {if (r !== 'backdropClick') onClose();}} maxWidth="md" fullWidth
    TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Create Purchase Order
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 0, pt: '12px !important' }}>
        {order &&
        <Box sx={{ backgroundColor: '#F5F5F5', borderRadius: '6px', px: 2, py: 1.25, mb: 2, display: 'flex', gap: 3 }}>
            <Box>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Order #</Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>{order.orderNumber}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stock Location</Typography>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{order.stockLocation ?? '—'}</Typography>
            </Box>
          </Box>
        }

        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Supplier *</Typography>
        <FormControl size="small" fullWidth sx={{ ...fieldSx, mb: 2.5 }}>
          <InputLabel>Supplier</InputLabel>
          <Select label="Supplier" value={vendorName} onChange={(e) => setVendorName(e.target.value)}>
            {activeSuppliers.map((s) =>
            <MenuItem key={s.id} value={s.name} sx={{ fontSize: '0.875rem' }}>{s.name}</MenuItem>
            )}
          </Select>
        </FormControl>
        <Divider sx={{ mb: 2.5 }} />

        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.25 }}>Parts *</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
          {lineItems.map((li, idx) => {
            const rowTotal = (parseFloat(li.unitCost) || 0) * (parseInt(li.quantity, 10) || 0);
            return (
              <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <FormControl size="small" required sx={{ flex: '1.2 1 0', ...fieldSx }}>
                  <InputLabel>Part #</InputLabel>
                  <Select label="Part #" value={li.partNumber} onChange={(e) => handlePartChange(idx, e.target.value)}>
                    {parts.map((p) =>
                    <MenuItem key={p.partNumber} value={p.partNumber} sx={{ fontSize: '0.875rem' }}>{p.partNumber}</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <TextField size="small" label="Description" value={li.partDescription}
                onChange={(e) => updateLine(idx, 'partDescription', e.target.value)}
                sx={{ flex: '1.8 1 0', ...fieldSx }} />
                <TextField size="small" label="Qty" required type="number" value={li.quantity}
                onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                inputProps={{ min: 1 }} sx={{ flex: '0.5 1 0', minWidth: 64, ...fieldSx }} />
                <TextField size="small" label="Unit Cost ($)" type="number" value={li.unitCost}
                onChange={(e) => updateLine(idx, 'unitCost', e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
                helperText={rowTotal > 0 ? fmtCurrency(rowTotal) : ''}
                sx={{ flex: '0.8 1 0', minWidth: 100, ...fieldSx }} />
                <Box sx={{ pt: '4px' }}>
                  <IconButton size="small" onClick={() => removeLine(idx)} disabled={lineItems.length === 1}
                  sx={{ color: lineItems.length === 1 ? 'transparent' : 'var(--grey-400)', '&:hover': { color: '#d32f2f' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>);

          })}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={addLine}
          sx={{ color: '#009688', textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', p: 0, '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}>
            Add Part
          </Button>
          {grandTotal > 0 &&
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)' }}>
              Total: <strong>{fmtCurrency(grandTotal)}</strong>
            </Typography>
          }
        </Box>
        <Divider sx={{ mb: 2.5 }} />

        <TextField size="small" label="Notes / Special Instructions (optional)" value={notes}
        onChange={(e) => setNotes(e.target.value)} multiline rows={2} fullWidth sx={fieldSx} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography onClick={onClose} sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          Cancel
        </Typography>
        <Button variant="contained" size="small" disabled={!isValid} onClick={handleSave}
        sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2 }}>
          Create PO
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PartReplenishmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [createVsOpen, setCreateVsOpen] = useState(false);
  const [localShipment, setLocalShipment] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMsg, setBannerMsg] = useState('');
  const [createPoOpen, setCreatePoOpen] = useState(false);
  const [localLinkedPoId, setLocalLinkedPoId] = useState(null);
  const [localLinkedPoNum, setLocalLinkedPoNum] = useState(null);
  const [localMessages, setLocalMessages] = useState(
    () => partReplenishmentMessages.filter((m) => m.replenishmentId === id)
  );

  const order = partReplenishmentOrders.find((o) => o.id === id);

  // Effective linked PO — local session state takes priority over mock data
  const effectiveLinkedPoId = localLinkedPoId ?? order?.linkedPurchaseOrderId ?? null;
  const effectiveLinkedPoNum = localLinkedPoNum ?? order?.linkedPurchaseOrderNumber ?? null;

  // resolvedRecipient — supplier contact
  const resolvedRecipient = useMemo(() => {
    if (!order) return null;
    const sup = suppliers.find((s) => s.name === order.vendorName);
    return sup ? { name: sup.contactName, email: sup.contactEmail } : null;
  }, [order]);

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          title="Order not found"
          description={`No part replenishment order with ID "${id}" exists in the system.`} />
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography
            onClick={() => navigate('/orders/part-replenishment')}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            
            ← Back to Part Replenishment
          </Typography>
        </Box>
      </Box>);

  }

  const mockShipment = order.vendorShipmentId ?
  vendorShipments.find((vs) => vs.id === order.vendorShipmentId) :
  null;
  const effectiveShipment = localShipment ?? mockShipment;

  const handleSendMessage = (text) => {
    const msg = { id: `rep-msg-local-${Date.now()}`, replenishmentId: id, sender: 'coordinator', senderName: 'You', text, sentAt: new Date().toISOString() };
    setLocalMessages((prev) => [...prev, msg]);
  };

  const timelineEvents = buildTimeline(order, Boolean(effectiveShipment));

  const warehouse = warehouses.find((w) => w.name === order.stockLocation);

  const handleCreateShipment = (newShipment) => {
    setLocalShipment(newShipment);
    setCreateVsOpen(false);
    setBannerMsg(`Vendor shipment ${newShipment.shipmentNumber} created and linked to ${order.orderNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const handleCreatePO = (newPO) => {
    setLocalLinkedPoId(newPO.id);
    setLocalLinkedPoNum(newPO.poNumber);
    setCreatePoOpen(false);
    setBannerMsg(`Purchase order ${newPO.poNumber} created and linked to ${order.orderNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const typeColors = {
    FSL: { bg: '#E3F2FD', color: '#1565C0' },
    Warehouse: { bg: '#E0F2F1', color: '#00695C' },
    'On-Site': { bg: '#FFF3E0', color: '#E65100' }
  };
  const tc = typeColors[warehouse?.type] ?? { bg: 'var(--grey-100)', color: 'var(--grey-600)' };

  const stockLocationFields = [
  {
    label: 'Location',
    value: warehouse ?
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)' }}>{order.stockLocation}</Typography>
          <Box sx={{ px: '8px', py: '2px', borderRadius: '10px', backgroundColor: tc.bg }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: tc.color, lineHeight: 1 }}>
              {warehouse.type}
            </Typography>
          </Box>
        </Box> :
    order.stockLocation || '—'
  },
  ...(warehouse ? [
  { label: 'Address', value: `${warehouse.address}, ${warehouse.city}, ${warehouse.state}` },
  { label: 'Contact Name', value: warehouse.contactName },
  { label: 'Contact Email', value: warehouse.contactEmail }] :
  [])];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Success banner */}
      {showBanner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {bannerMsg}
          </Typography>
        </Box>
      }

      {/* Header */}
      <BreadcrumbHeader
        title={order.orderNumber}
        subtitle={order.vendorName ?? order.supplierName ?? ''}
        crumbs={[
        { label: 'Orders' },
        { label: 'Part Replenishment', path: '/orders/part-replenishment' },
        { label: order.orderNumber }]
        } />

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
        {/* Left column — 60% */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 3, minWidth: 0 }}>
          <OrderInfoCard order={order} />
          <DetailPanel title="Stock Location" fields={stockLocationFields} />
          <ChatPanel messages={localMessages} recipient={resolvedRecipient} onSend={handleSendMessage} />
        </Box>

        {/* Right column — 40% */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 2, minWidth: 0 }}>
          <VendorShipmentCard
            shipment={effectiveShipment}
            onCreateClick={() => setCreateVsOpen(true)} />
          
          <LinkedPOCard
            poId={effectiveLinkedPoId}
            poNumber={effectiveLinkedPoNum}
            onCreateClick={() => setCreatePoOpen(true)} />
          
          <ActivityTimeline events={timelineEvents} />
        </Box>
      </Box>

      {/* Create Vendor Shipment — shared modal (prefilled from order) */}
      <CreateVendorShipmentModal
        open={createVsOpen}
        onClose={() => setCreateVsOpen(false)}
        onSubmit={handleCreateShipment}
        prefill={{
          partReplenishmentOrderId: order.id,
          partReplenishmentOrderNumber: order.orderNumber,
          vendorName: order.vendorName,
          stockLocation: order.stockLocation ?? ''
        }} />

      {/* Create PO — linked to this replenishment order */}
      <CreatePOModal
        open={createPoOpen}
        order={order}
        onClose={() => setCreatePoOpen(false)}
        onSave={handleCreatePO} />

    </Box>);

}